<script setup lang="ts">
/**
 * Shared, data-driven constellation renderer (M4C-26).
 *
 * Renders accessible SVG from backend-compatible geometry (`stars_data` /
 * `segments_data`, normalized 0–100) plus an optional per-member progress
 * overlay. The same component backs the overview cards (M4C-20), the detail view
 * (M4C-21), the Star Glazing explainer (M4C-28), reward feedback (M4C-29) and the
 * community profile rows (M4C-34) — only `variant` and a few props change.
 *
 * It embeds **no** constellation coordinates: geometry is always passed in, so it
 * works unchanged with any backend-created constellation. Colour is entirely
 * M4C-23 Tailwind tokens (hex-free).
 *
 * @example Detail view (M4C-21) from a ConstellationDetail
 * <GamiConstellation
 *   :stars="detail.constellation.stars_data"
 *   :segments="detail.constellation.segments_data"
 *   :progress="detail.stars"
 *   :current-fragments="detail.current_fragments"
 *   :name="detail.name"
 *   variant="detail"
 * />
 */
import { computed } from 'vue';
import type {
  ApiConstellationSegment,
  ApiConstellationStar,
  ConstellationStarProgress,
} from '@/types/gamification';
import {
  normalizeConstellation,
  type ConstellationRenderVariant,
  type StarRenderState,
} from '@/composables/gamification/constellation-render';

const props = withDefaults(
  defineProps<{
    /** Raw star geometry (normalized 0–100). Required. */
    stars: ApiConstellationStar[];
    /** Outline segments, resolved by star id. */
    segments?: ApiConstellationSegment[];
    /** Per-member star states (earned/current/locked) from the data layer. */
    progress?: ConstellationStarProgress[];
    /** Member's cumulative fragments — fills the current star's progress ring. */
    currentFragments?: number;
    /** Explicit per-star-id render-state overrides (static/teaching views). */
    states?: Partial<Record<string, StarRenderState>>;
    /** Star id to emphasise as just-unlocked. */
    highlightStarId?: string | null;
    /** Presentation preset (sizes + label defaults). */
    variant?: ConstellationRenderVariant;
    /** Force star labels on/off; defaults to the variant's preference. */
    showLabels?: boolean;
    /** Constellation name, used to build the accessible label when none is given. */
    name?: string;
    /** Accessible label override for the whole figure. */
    label?: string;
    /** Idle twinkle / pulse animations (still respects prefers-reduced-motion). */
    animated?: boolean;
  }>(),
  {
    segments: () => [],
    variant: 'detail',
    highlightStarId: null,
    animated: true,
  },
);

/** Per-variant sizing, in the fixed 0–100 viewBox units. `star` is the coin radius. */
const VARIANT_PRESETS: Record<
  ConstellationRenderVariant,
  { star: number; stroke: number; labels: boolean; labelSize: number }
> = {
  card: { star: 5, stroke: 1.2, labels: false, labelSize: 4 },
  detail: { star: 5.5, stroke: 1.4, labels: true, labelSize: 4.2 },
  explainer: { star: 5.5, stroke: 1.4, labels: true, labelSize: 4.2 },
  celebration: { star: 6.8, stroke: 1.7, labels: false, labelSize: 4 },
  row: { star: 4.4, stroke: 1, labels: false, labelSize: 3.4 },
};

const preset = computed(() => VARIANT_PRESETS[props.variant]);
const showLabels = computed(() => props.showLabels ?? preset.value.labels);

const model = computed(() =>
  normalizeConstellation(props.stars, props.segments ?? [], {
    progress: props.progress,
    currentFragments: props.currentFragments,
    states: props.states,
    highlightStarId: props.highlightStarId,
  }),
);

const accessibleLabel = computed(() => {
  if (props.label) {
    return props.label;
  }
  const subject = props.name ? `${props.name} constellation` : 'Constellation';
  return `${subject}, ${model.value.earnedCount} of ${model.value.total} stars earned`;
});
</script>

<template>
  <div class="gami-constellation block aspect-square w-full" :class="`gami-constellation--${variant}`">
    <svg
      class="h-full w-full overflow-visible"
      :viewBox="model.viewBox"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      :aria-label="accessibleLabel"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{{ accessibleLabel }}</title>

      <!-- Connector lines, drawn behind the coins (decorative; meaning is on the figure). -->
      <g aria-hidden="true" stroke-linecap="round">
        <template v-for="seg in model.segments" :key="seg.id">
          <!-- Traced path: solid amber. -->
          <line
            v-if="seg.level === 'lit'"
            class="stroke-star"
            :x1="seg.x1"
            :y1="seg.y1"
            :x2="seg.x2"
            :y2="seg.y2"
            :stroke-width="preset.stroke"
          />
          <!-- Not yet reached: faint, dotted. -->
          <line
            v-else
            class="stroke-fontColor-w80"
            :x1="seg.x1"
            :y1="seg.y1"
            :x2="seg.x2"
            :y2="seg.y2"
            :stroke-width="preset.stroke * 0.55"
            stroke-dasharray="0.4 2.6"
            opacity="0.8"
          />
        </template>
      </g>

      <!-- Star coins. -->
      <g>
        <GamiConstellationStar
          v-for="star in model.stars"
          :key="star.id"
          :cx="star.cx"
          :cy="star.cy"
          :state="star.state"
          :radius="preset.star"
          :label="star.label"
          :show-label="showLabels"
          :label-size="preset.labelSize"
          :highlighted="star.highlighted"
          :animated="animated"
        />
      </g>
    </svg>
  </div>
</template>
